import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useDriver } from "./DriverContext";
import { Input } from "./Input";
import defaultAvatar from "../../../../assets/images/default_avatar.jpg";
import { DeliveryAgentInterface } from "../OrdersDeliveryBoysView";
import transparentBg from "../../../../assets/images/transparent-bg.png";
import toast from "react-hot-toast";

type DeliveryBoyPopUpDetailsProps = {
  driver: DeliveryAgentInterface;
  onCancel: () => void;
};

export const DeliveryBoyPopUpDetails: React.FC<
  DeliveryBoyPopUpDetailsProps
> = ({ driver, onCancel }) => {
  const { state, dispatch } = useDriver();
  const { personalInfo, vehicleInfo } = state;

  useEffect(() => {
    if (driver) {
      dispatch({
        type: "UPDATE_PERSONAL_INFO",
        payload: {
          id: driver.id,
          name: driver.name,
          phoneNumber: driver.mobileNumber,
          profilePicture: driver.profilePicture,
          education: driver.education,
          dateOfBirth: driver.dateOfBirth?.split("T")[0], // Format date
          aadharNumber: driver.aadharNumber,
          address: driver.address,
          email: driver.email,
        },
      });

      dispatch({
        type: "UPDATE_VEHICLE_INFO",
        payload: {
          vehicleBrand: driver.vehicleBrand,
          vehicleModel: driver.vehicleBrand, // Assuming same as brand for now
          vehicleNumber: driver.vehicleNumber,
          driverLicenseNumber: driver.drivingLicence,
        },
      });
    }
  }, [driver, dispatch]);

  const handleSave = () => {
    console.log("Saving driver information:", { personalInfo, vehicleInfo });
    toast.success("Driver information saved successfully!");
    dispatch({ type: "RESET" });
    onCancel();
  };

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-cover bg-center flex  justify-center px-2 pb-2 overflow-hidden overflow-y-auto"
        style={{
          backgroundImage: `url(${transparentBg})`,
        }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={onCancel}
        />

        {/* Modal Container */}
        <div className="relative top-3 w-full max-w-2xl max-h-[90vh] mx-4 bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Scrollable Content */}
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Fixed Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
              <h2 className="text-lg font-medium text-green-600">
                Driver Details
              </h2>
              <X
                className="w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600"
                onClick={onCancel}
              />
            </div>

            {/* Scrollable Body */}
            <div
              className="flex-1 overflow-y-auto p-6 space-y-6"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
              }}
            >
              {/* Profile Section */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200">
                    <img
                      src={personalInfo.profilePicture || defaultAvatar}
                      alt="Driver"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = defaultAvatar;
                      }}
                    />
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  <Input
                    label="Name"
                    value={personalInfo.name}
                    onChange={() => {}}
                    readOnly
                  />

                  <Input
                    label="Phone Number"
                    value={personalInfo.phoneNumber}
                    onChange={() => {}}
                    readOnly
                  />
                </div>
                <Input
                  label="Email"
                  value={personalInfo.email}
                  onChange={() => {}}
                  readOnly
                />
              </div>

              {/* Personal Info Section */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Education"
                    value={personalInfo.education}
                    onChange={() => {}}
                    readOnly
                  />

                  <Input
                    label="Date of Birth"
                    value={personalInfo.dateOfBirth}
                    onChange={() => {}}
                    readOnly
                    type="date"
                  />
                </div>

                <Input
                  label="Aadhar Number"
                  value={personalInfo.aadharNumber}
                  onChange={() => {}}
                  readOnly
                />

                <Input
                  label="Address"
                  value={personalInfo.address}
                  onChange={() => {}}
                  readOnly
                />
              </div>

              {/* Vehicle Info Section */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-700">
                  Vehicle Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Vehicle Brand"
                    value={vehicleInfo.vehicleBrand}
                    onChange={() => {}}
                    readOnly
                  />
                  <Input
                    label="Vehicle Model"
                    value={vehicleInfo.vehicleModel}
                    onChange={() => {}}
                    readOnly
                  />
                  <Input
                    label="Vehicle Number"
                    value={vehicleInfo.vehicleNumber}
                    onChange={() => {}}
                    readOnly
                  />
                  <Input
                    label="Driver License Number"
                    value={vehicleInfo.driverLicenseNumber}
                    onChange={() => {}}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-white">
              <button
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                onClick={onCancel}
              >
                Close
              </button>
              {/* <button
                onClick={handleSave}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Save
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
